import {
  FormControl,
  InputLabel,
  MenuItem,
  Select as MUISelect,
  SelectChangeEvent,
  SelectProps as MUISelectProps
} from '@mui/material'
import {
  FieldWrapper,
  FieldWrapperProps,
  LoadingWrapper,
  TextOrTranslation
} from '.'
import {
  LocalizedOptionsKeyType,
  useFieldProps,
  useLocalizedOptionsValue
} from '../hooks'
import { getElementId } from '../utils'
import { Maybe } from '../utils/classes/graphql'
import { CommonFieldProps, SelectOption } from './types/types'

type MaybeSelectOption<T extends SelectOption> = Maybe<T>

interface SelectProps<T extends SelectOption>
  extends Partial<FieldWrapperProps>,
    Omit<MUISelectProps, 'label' | 'name' | 'onChange'>,
    CommonFieldProps {
  options: MaybeSelectOption<T>[]
}

export const Select = <T extends SelectOption>({
  executeOnChangeFn,
  gridProps,
  label,
  labelTranslationId,
  options,
  hidefield = false,
  name,
  value,
  ...rest
}: SelectProps<T>) => {
  const elementId = getElementId()
  const labelId = elementId + '-label'

  return (
    <FieldWrapper hidefield={hidefield} gridProps={gridProps || { md: 4 }}>
      <FormControl>
        <InputLabel id={labelId} htmlFor={elementId} variant="standard">
          <TextOrTranslation text={label} translationId={labelTranslationId} />
        </InputLabel>
        <LoadingWrapper isLoading={options == null}>
          <MUISelect
            {...rest}
            onChange={executeOnChangeFn}
            value={value || ''}
            defaultValue=""
            labelId={labelId}
            id={elementId}
            name={name}
            variant="standard"
          >
            {options?.map((option: MaybeSelectOption<T>) => {
              if (option?.value) {
                return (
                  <MenuItem key={option.value.toString()} value={option.value}>
                    <TextOrTranslation
                      text={option.display}
                      translationId={option.translationId}
                    />
                  </MenuItem>
                )
              }
              return null
            })}
          </MUISelect>
        </LoadingWrapper>
      </FormControl>
    </FieldWrapper>
  )
}

type FormikSelectProps<T extends SelectOption> = Omit<SelectProps<T>, 'value'>

export const FormikSelect = <T extends SelectOption>({
  name,
  executeOnChangeFn,
  ...rest
}: FormikSelectProps<T>) => {
  const { onChange: fieldOnChange, value } = useFieldProps(name)

  const handleChange = (event: SelectChangeEvent<unknown>) => {
    fieldOnChange(event)
    if (executeOnChangeFn) {
      executeOnChangeFn(event)
    }
  }

  return (
    <Select
      {...rest}
      name={name}
      executeOnChangeFn={(e) => handleChange(e)}
      value={value}
    />
  )
}

interface LocalizedSelectProps<T extends SelectOption>
  extends Omit<FormikSelectProps<T>, 'options'> {
  localizedOptionsKey: LocalizedOptionsKeyType
}

export const LocalizedSelect = <T extends SelectOption>({
  localizedOptionsKey,
  ...props
}: LocalizedSelectProps<T>) => {
  const options = useLocalizedOptionsValue(localizedOptionsKey)

  return <FormikSelect {...props} options={options} />
}
